import { Play, Pause, Square, SkipBack, SkipForward, Mic } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { HOW_IT_WORKS } from '@/constants/testIds';
import { STAGES, AUDIENCE_LABELS } from '@/lib/howItWorksContent';

// Voice guide shell. Narration audio requires an ElevenLabs API key which has
// not been configured yet, so transport controls are shown but disabled with
// a clear "coming soon" affordance. The stage list stays fully functional as
// a jump-to-stage index into the timeline (real feature, no audio needed).
export const VoiceGuidePlayer = ({ audience, activeStage, onStageSelect }) => {
  return (
    <TooltipProvider>
      <Card
        id="voice-guide"
        data-testid={HOW_IT_WORKS.voiceSection}
        className="border-[hsl(var(--lxra-border))] bg-card"
      >
        <CardContent className="p-6 sm:p-7">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2">
              <Mic className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
              <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-muted))]">Voice Guide</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                data-testid={HOW_IT_WORKS.voiceAudienceBadge}
                className="bg-[hsl(var(--lxra-gold)/0.14)] text-[hsl(var(--lxra-cream))] border border-[hsl(var(--lxra-gold)/0.35)] font-normal"
              >
                {AUDIENCE_LABELS[audience]}
              </Badge>
              <Badge
                data-testid={HOW_IT_WORKS.voiceComingSoonBadge}
                variant="secondary"
                className="font-normal"
              >
                Narration coming soon
              </Badge>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mb-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled
                    aria-label="Previous stage narration"
                    data-testid={HOW_IT_WORKS.voicePrev}
                  >
                    <SkipBack className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Voice narration coming soon</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="default"
                    size="icon"
                    disabled
                    aria-label="Play voice guide"
                    data-testid={HOW_IT_WORKS.voicePlayPause}
                    className="bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] disabled:opacity-40"
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Voice narration coming soon</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="secondary"
                    size="icon"
                    disabled
                    aria-label="Stop voice guide"
                    data-testid={HOW_IT_WORKS.voiceStop}
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Voice narration coming soon</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled
                    aria-label="Next stage narration"
                    data-testid={HOW_IT_WORKS.voiceNext}
                  >
                    <SkipForward className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Voice narration coming soon</TooltipContent>
            </Tooltip>
          </div>

          <Progress value={0} data-testid={HOW_IT_WORKS.voiceProgress} className="mb-6" />

          <p className="text-xs text-[hsl(var(--lxra-muted))] mb-3">
            Tap a stage to jump to it on the timeline below.
          </p>
          <div className="space-y-1.5">
            {STAGES.map((stage) => {
              const isActive = activeStage === stage.slug;
              return (
                <button
                  key={stage.slug}
                  type="button"
                  onClick={() => onStageSelect(stage.slug)}
                  data-testid={HOW_IT_WORKS.voiceStageButton(stage.slug)}
                  className={`w-full text-left flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150 border ${
                    isActive
                      ? 'bg-[hsl(var(--lxra-gold)/0.10)] border-[hsl(var(--lxra-gold)/0.28)] text-[hsl(var(--lxra-fg))]'
                      : 'border-transparent text-[hsl(var(--lxra-muted))] hover:bg-[hsl(var(--lxra-bg-2))]'
                  } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lxra-gold)/0.55)]`}
                >
                  <span>{stage.title}</span>
                  {isActive && <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--lxra-gold))]" />}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
